import {
  CheckCircleIcon,
  ChevronDownIcon,
  CreditCardIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import ProductUpdateAnnouncementModal from "../announcements/ProductUpdateAnnouncementModal";

interface FamilyBillingAnnouncementModalProps {
  enabled: boolean;
  studioId: string | null;
  viewerId: string | null;
  forceOpen?: boolean;
  onDismiss?: () => void;
}

export const FAMILY_BILLING_UPDATE = {
  announcementId: "family-billing-announcement",
  version: "v3",
  publishedLabel: "October 2026",
  title: "One monthly bill for each family and student",
  description:
    "Consolidated billing puts every student, and everyone sharing a Family ID, on one Stripe subscription with one renewal date. Plus easier plan switches, birthdays from waivers, and an updated Ask Sifu.",
} as const;

const FAMILY_BILLING_FAQS = [
  {
    question: "I don't see a Consolidate button. Why?",
    answer:
      "Consolidate billing only appears for a student who is not in a family and has two or more active Stripe subscriptions. Consolidate family billing only appears on the Billing primary's card. During the rollout, these buttons are turned on for selected students and families. Contact support@dojotrack.com if one you need is missing.",
  },
  {
    question: "How do I know who the Billing primary is?",
    answer:
      "Open the student card for anyone who shares the Family ID and look under Family Management. In the Family Members list, the person responsible for the family's Stripe billing has Billing primary next to their name. Other automatic-billing members show Billed through [primary name] on their profiles.",
  },
  {
    question: "Will my studio lose money when I consolidate?",
    answer:
      "DojoTrack keeps each membership's already-paid coverage and previews any one-time billing-cycle alignment amount needed to reach the family's shared renewal date. Only uncovered days are prorated, after the family discount. That amount is not charged separately when you consolidate: it is added to the Billing primary's first consolidated family invoice, or the next invoice if the family already has a consolidated subscription. If the dates already align, the amount is $0; if the dates cannot be verified safely, consolidation stops.",
  },
  {
    question: "What happens when a family member leaves?",
    answer:
      "Only that student's memberships leave the shared family subscription. DojoTrack creates their individual Stripe subscriptions on the same renewal date, so a new billing cycle does not start. Paused memberships stay paused, scheduled cancellations keep their cancellation date, and the remaining family's discounts are recalculated. If the student needs a payment method, DojoTrack keeps the handoff ready to finish after one is added.",
  },
  {
    question: "Can I pause or cancel memberships for one student?",
    answer:
      "Yes. Pause stops billing for all memberships assigned to that student while the rest of the family continues normally. Resume restores them to the existing family schedule without charging for already-paid time again. You can also cancel an individual membership; it normally stays active through its paid period and is removed before the next renewal.",
  },
  {
    question: "Do family members share account credits?",
    answer:
      "Yes. With consolidated billing, credits and debits live on the Billing primary's Stripe account and apply to the shared family invoice. Credit a student brings into the family is tracked, and any still-available credit attributed to that student returns to their individual account if they leave. Make family balance adjustments from the Billing primary's profile.",
  },
  {
    question: "What happens if I add a membership during a billing cycle?",
    answer:
      "DojoTrack previews the prorated amount for the days remaining until the family's next renewal, including the student's family discount. Stripe charges that partial amount to the Billing primary's card, then includes the normal recurring family rate on future shared invoices.",
  },
  {
    question: "Can a family mix Cash/Manual and Automatic Billing?",
    answer:
      "Yes. Automatic memberships go on the consolidated Stripe subscription and use the Billing primary's card. Cash/manual memberships stay outside Stripe and must be collected and recorded separately. Family discounts still factor in eligible students across both billing modes: the applicable discount appears in the student's cash/manual pricing or on their automatic membership in the family Stripe bill. Cash/manual students can count toward the family discount order, but the Billing primary for Stripe must use automatic billing.",
  },
  {
    question: "What happens if the Billing primary leaves?",
    answer:
      "DojoTrack moves the remaining family memberships, renewal date, and family balance to an eligible automatic-billing student with a payment method. The former Billing primary's own memberships move to individual subscriptions with their renewal date, pause, and scheduled-cancellation status preserved. If no eligible replacement has a payment method, DojoTrack stops the change and explains what must be fixed.",
  },
];

export default function FamilyBillingAnnouncementModal({
  enabled,
  studioId,
  viewerId,
  forceOpen = false,
  onDismiss,
}: FamilyBillingAnnouncementModalProps) {
  const navigate = useNavigate();

  return (
    <ProductUpdateAnnouncementModal
      enabled={enabled}
      announcementId={FAMILY_BILLING_UPDATE.announcementId}
      version={FAMILY_BILLING_UPDATE.version}
      audienceScope={studioId && viewerId ? [studioId, viewerId] : null}
      forceOpen={forceOpen}
      onDismiss={onDismiss}
      title={FAMILY_BILLING_UPDATE.title}
      description={FAMILY_BILLING_UPDATE.description}
      icon={<UserGroupIcon className="h-7 w-7" aria-hidden="true" />}
      closeLabel="Close family billing announcement"
      primaryAction={{
        label: "Go to Student Management",
        onClick: () => navigate("/app/students"),
      }}
    >
            <section>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                How family billing works
              </h3>
              <div className="mt-3 space-y-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
                <div className="flex gap-3">
                  <CreditCardIcon
                    className="mt-0.5 h-5 w-5 flex-none text-indigo-600 dark:text-indigo-400"
                    aria-hidden="true"
                  />
                  <p>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      Every family has one Billing primary.
                    </span>{" "}
                    The family&apos;s recurring Stripe payment uses the card saved on
                    that person&apos;s account.
                  </p>
                </div>
                <div className="flex gap-3">
                  <CheckCircleIcon
                    className="mt-0.5 h-5 w-5 flex-none text-green-600 dark:text-green-400"
                    aria-hidden="true"
                  />
                  <p>
                    Other family members show{" "}
                    <span className="font-medium">
                      Billed through [primary name]
                    </span>
                    . Their memberships remain on their own student profiles, so
                    each student can still be managed separately.
                  </p>
                </div>
                <div className="flex gap-3">
                  <CheckCircleIcon
                    className="mt-0.5 h-5 w-5 flex-none text-green-600 dark:text-green-400"
                    aria-hidden="true"
                  />
                  <p>
                    The family shares one renewal date and one regular monthly
                    invoice. Configured family discounts stay attached to the
                    appropriate memberships, while family credits and debits are
                    handled on the Billing primary&apos;s account.
                  </p>
                </div>
              </div>
            </section>

            <section className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  One family payment
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-400">
                  Families see one regular monthly payment instead of several
                  separate recurring charges.
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Simpler administration
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-400">
                  Staff manage one payment method, renewal date, and family invoice.
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Fewer Stripe transactions
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-400">
                  Replacing separate recurring charges with one family payment may
                  reduce transaction-based processing fees.
                </p>
              </div>
            </section>

            <section className="rounded-xl border border-indigo-200 bg-indigo-50/70 p-5 dark:border-indigo-800 dark:bg-indigo-950/30">
              <h3 className="text-base font-semibold text-indigo-950 dark:text-indigo-100">
                How to consolidate an existing family
              </h3>
              <ol className="mt-3 list-decimal space-y-3 pl-5 text-sm leading-6 text-indigo-950/80 marker:font-bold marker:text-indigo-600 dark:text-indigo-100/80 dark:marker:text-indigo-300">
                <li className="pl-1">
                  In <span className="font-semibold">Student Management</span>, open
                  any student who is already part of the family you want to
                  consolidate.
                </li>
                <li className="pl-1">
                  In the student card, under{" "}
                  <span className="font-semibold">Family Management</span>, review
                  the Family ID and Family Members list. Make sure every student who
                  should be included uses that same Family ID.
                </li>
                <li className="pl-1">
                  In the Family Members list, find the person whose name has the{" "}
                  <span className="font-semibold">Billing primary</span> label. This
                  is the student whose Stripe account will hold the shared family
                  subscription.
                </li>
                <li className="pl-1">
                  Open that Billing primary&apos;s student profile and confirm that
                  they have a payment method on file.
                </li>
                <li className="pl-1">
                  In the Billing primary&apos;s student card, under{" "}
                  <span className="font-semibold">Family Management</span>, find{" "}
                  <span className="font-semibold">Consolidated Family Billing</span>{" "}
                  and select{" "}
                  <span className="font-semibold">Consolidate family billing</span>.
                </li>
                <li className="pl-1">
                  Review the memberships, family discounts, and any one-time
                  billing-cycle alignment charge in the preview. Press{" "}
                  <span className="font-semibold">OK</span> to consolidate, or{" "}
                  <span className="font-semibold">Cancel</span> to stop without
                  changing anything.
                </li>
              </ol>
              <p className="mt-4 border-t border-indigo-200 pt-3 text-xs leading-5 text-indigo-800 dark:border-indigo-800 dark:text-indigo-200">
                DojoTrack then cancels the old individual Stripe subscriptions and
                moves their memberships onto the Billing primary&apos;s family
                subscription. If a subscription is paused, past due, or needs
                attention, DojoTrack will stop and explain what to resolve first.
              </p>
            </section>

            <section className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-900/40">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                How to consolidate a single student
              </h3>
              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Students set up before this update may still have a separate Stripe
                subscription for each membership. New students already get one.
              </p>
              <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-6 text-gray-600 marker:font-bold marker:text-indigo-600 dark:text-gray-300 dark:marker:text-indigo-300">
                <li className="pl-1">
                  Open the student in{" "}
                  <span className="font-semibold">Student Management</span>. They
                  must not be in a family.
                </li>
                <li className="pl-1">
                  Under their memberships, select{" "}
                  <span className="font-semibold">Consolidate billing</span>.
                </li>
                <li className="pl-1">
                  Review the preview, then press{" "}
                  <span className="font-semibold">OK</span>.
                </li>
              </ol>
              <p className="mt-3 text-xs leading-5 text-gray-500 dark:text-gray-400">
                Memberships move onto the subscription that renews last, and the
                others are canceled. Nothing is charged when you consolidate. The
                preview shows any gap to line up dates; charge it with One-Time
                Payment if you want to collect it. Paused and scheduled-to-cancel
                subscriptions are left as they are.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Also new
              </h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Switching memberships
                  </p>
                  <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-400">
                    Uncheck one membership and check another in the same save. The
                    card isn&apos;t charged or credited now; the new price starts at
                    the next renewal, on the same date.
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Birthdays from waivers
                  </p>
                  <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-400">
                    A signed waiver&apos;s date of birth is saved on the new lead.
                    Leads also have Birthday and Gender fields, and both carry over,
                    with age, when the lead becomes a student.
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Ask Sifu is up to date
                  </p>
                  <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-400">
                    The chat bubble in the bottom right now answers questions about
                    consolidating, family credits, and switching memberships.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Common family billing questions
              </h3>
              <div className="mt-3 divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
                {FAMILY_BILLING_FAQS.map(({ question, answer }) => (
                  <details key={question} className="group px-4 sm:px-5">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-left text-sm font-semibold text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 dark:text-white [&::-webkit-details-marker]:hidden">
                      <span>{question}</span>
                      <ChevronDownIcon
                        className="h-4 w-4 flex-none text-gray-500 transition-transform group-open:rotate-180 dark:text-gray-400"
                        aria-hidden="true"
                      />
                    </summary>
                    <p className="pb-4 pr-8 text-sm leading-6 text-gray-600 dark:text-gray-300">
                      {answer}
                    </p>
                  </details>
                ))}
              </div>
            </section>

      <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">
        Already see every family membership in the Consolidated Family Billing
        panel? That family is already set up and does not need to be migrated again.
      </p>
    </ProductUpdateAnnouncementModal>
  );
}
